import {
  Instant,
  LocalDate,
  LocalDateTime,
  Schema,
  stringToInstant,
  stringToLocalDate,
  stringToLocalDateTime,
} from "@kform/core";

// Default temporal values (UNIX Epoch `0`)
const DEFAULT_INSTANT = stringToInstant("1970-01-01T00:00:00Z");
const DEFAULT_LOCAL_DATE = stringToLocalDate("1970-01-01");
const DEFAULT_LOCAL_DATE_TIME = stringToLocalDateTime("1970-01-01T00:00:00");

/**
 * Supported date or time related types.
 */
export type Temporal = Instant | LocalDate | LocalDateTime | Date;

/**
 * Formats a temporal {@link value} as a string, given its {@link schema}.
 * @param value Temporal value to format as a string.
 * @param schema Schema of the temporal {@link value}.
 * @returns String representation of the temporal {@link value}.
 */
export function formatTemporalAsString<
  T extends Temporal | null = Temporal | null,
  // Not using the `schema` is an implementation detail
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
>(value: T | undefined, schema: Schema<T>): string {
  return isJsDate(value)
    ? Number.isNaN(+value)
      ? ""
      : value.toISOString()
    : (value?.toString() ?? "");
}

/**
 * Parses a formatted temporal [value]{@link formattedValue} as a temporal
 * value, given the {@link schema} of the expected temporal value.
 * @param formattedValue Formatted value to parse as a temporal value.
 * @param schema Schema of the expected temporal value.
 * @returns Temporal representation of the
 * [formatted value]{@link formattedValue} according to {@link schema}.
 */
export function parseStringAsTemporal<
  T extends Temporal | null = Temporal | null,
>(formattedValue: string, schema: Schema<T>): T {
  if (formattedValue === "" && schema.typeInfo.nullable) {
    return null as T;
  }

  switch (schema.typeInfo.name) {
    case "Instant":
      if (formattedValue === "") {
        return DEFAULT_INSTANT as T;
      }
      try {
        return stringToInstant(formattedValue) as T;
      } catch {
        return (schema.typeInfo.nullable ? null : DEFAULT_INSTANT) as T;
      }
    case "LocalDate":
      if (formattedValue === "") {
        return DEFAULT_LOCAL_DATE as T;
      }
      try {
        return stringToLocalDate(formattedValue) as T;
      } catch {
        return (schema.typeInfo.nullable ? null : DEFAULT_LOCAL_DATE) as T;
      }
    case "LocalDateTime":
      if (formattedValue === "") {
        return DEFAULT_LOCAL_DATE_TIME as T;
      }
      try {
        return stringToLocalDateTime(formattedValue) as T;
      } catch {
        return (schema.typeInfo.nullable ? null : DEFAULT_LOCAL_DATE_TIME) as T;
      }
    default: // JavaScript `Date`
      return new Date(formattedValue) as T;
  }
}

/**
 * Whether the value is a JavaScript {@link Date}.
 */
function isJsDate(value: unknown): value is Date {
  return (
    value instanceof Date ||
    (typeof value === "object" &&
      Object.prototype.toString.call(value) === "[object Date]")
  );
}
